import { assertDefined, chunkArray } from '@listening/utils/ts-utils';

export const LEGACY_CHAPTER_CHUNK_SIZE = 2;

export type LegacyChapterChunkParams = {
  audioId: string;
  cursor: string;
  limit: number;
};

export function legacyChapterChunkIndex(
  chapterIndex: number,
  chunkSize = LEGACY_CHAPTER_CHUNK_SIZE,
) {
  return Math.floor(chapterIndex / chunkSize);
}

// The cursor of chunk k is the last chapter id of chunk k-1, so every chunk is
// addressable from the document summary without walking earlier pages.
export function legacyChapterChunkParams(
  audioId: string,
  chapters: ReadonlyArray<{ id: string }>,
  chunkSize = LEGACY_CHAPTER_CHUNK_SIZE,
): ReadonlyArray<LegacyChapterChunkParams> {
  const chunks = chunkArray(chapters, chunkSize);
  return chunks.map((_, chunkIndex) => ({
    audioId,
    cursor:
      chunkIndex === 0
        ? ''
        : assertDefined(assertDefined(chunks[chunkIndex - 1]).at(-1)).id,
    limit: chunkSize,
  }));
}

export type LegacyChapterChunkWindow = {
  chunkCount: number;
  anchorChapterIndices: ReadonlyArray<number>;
  behind: number;
  ahead: number;
  alwaysFirstChunk: boolean;
  chunkSize?: number;
};

export function enabledLegacyChapterChunks({
  chunkCount,
  anchorChapterIndices,
  behind,
  ahead,
  alwaysFirstChunk,
  chunkSize = LEGACY_CHAPTER_CHUNK_SIZE,
}: LegacyChapterChunkWindow): ReadonlySet<number> {
  const enabled = new Set<number>();
  if (alwaysFirstChunk && chunkCount > 0) enabled.add(0);
  for (const chapterIndex of anchorChapterIndices) {
    if (chapterIndex < 0) continue;
    const chunkIndex = legacyChapterChunkIndex(chapterIndex, chunkSize);
    if (chunkIndex >= chunkCount) continue;
    const first = Math.max(0, chunkIndex - behind);
    const last = Math.min(chunkCount - 1, chunkIndex + ahead);
    for (let index = first; index <= last; index += 1) enabled.add(index);
  }
  return enabled;
}
